import React, { useState } from "react";
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Badge, Button, Card, Descriptions, message, Space, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { TraineeStatus } from "@fitness-app/data-models/entities/Trainee";

import ModalForm from "~/components/ModalForm/ModalForm";
import {
  defaultNotificationsSettings,
  NOTIFICATIONS_FREQUENCY_LABELS,
  type ClientNotificationsSettingsFormModel,
} from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeSettings/TraineeNotificationsData";
import { useTraineeProfile, useTraineeSettings } from "~/modules/Trainee/TraineeProfile/scope";
import TraineeNotificationsSettingsForm from "./TraineeNotificationsSettingsForm";
import { useUpdateTraineeSettings } from "./useUpdateTraineeSettings";

const TraineeNotifications = () => {
  const { t } = useTranslation(["trainees", "surveys"]);
  const { data: traineeSettings, isLoading: settingsLoading } = useTraineeSettings();
  const { data: traineeData } = useTraineeProfile();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const updateSettings = useUpdateTraineeSettings("notificationsSettings");

  if (traineeData?.status !== TraineeStatus.ACTIVE) {
    return (
      <div style={{ padding: 20 }}>
        <Alert message={t("shouldHasActiveStatus")} description={t("featureNotAvailable")} showIcon type="warning" />
      </div>
    );
  }

  const clientNotificationsSettings = {
    ...defaultNotificationsSettings,
    ...traineeSettings?.notificationsSettings,
  };

  const handleSubmit = async (formData: ClientNotificationsSettingsFormModel) => {
    try {
      await updateSettings.mutateAsync({
        ...formData,
        allowNotifications: clientNotificationsSettings.allowNotifications,
      });
      setIsFormVisible(false);
    } catch {
      void message.error("Nie udało się zapisać ustawień powiadomień. Spróbuj ponownie.");
    }
  };

  return (
    <Card
      bordered={false}
      loading={settingsLoading}
      className="!shadow-none"
      extra={
        clientNotificationsSettings ? (
          <Button size="small" icon={<PlusOutlined />} type="primary" onClick={() => setIsFormVisible(true)}>
            {t("notifications.editSettings")}
          </Button>
        ) : null
      }
    >
      <Descriptions title={t("notifications.settings")} bordered column={1} labelStyle={{ width: 350 }}>
        <Descriptions.Item label={t("notifications.allowNotifications")}>
          <Space align="center" size={12}>
            <Badge
              text={
                clientNotificationsSettings.allowNotifications === null
                  ? t("notifications.notChecked")
                  : clientNotificationsSettings.allowNotifications
                    ? t("notifications.active")
                    : t("notifications.inactive")
              }
              status={
                clientNotificationsSettings.allowNotifications === null
                  ? "warning"
                  : clientNotificationsSettings.allowNotifications
                    ? "processing"
                    : "error"
              }
            />
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t("notifications.notificationsAboutTraining")}>
          <Space align="center" size={12}>
            <Badge
              text={
                clientNotificationsSettings.activeWorkoutsReminder
                  ? t("notifications.active")
                  : t("notifications.inactive")
              }
              status={clientNotificationsSettings.activeWorkoutsReminder ? "processing" : "warning"}
            />
            {clientNotificationsSettings.activeWorkoutsReminder && (
              <Tooltip title={t("notifications.aboutTrainingNotification")}>
                <InfoCircleOutlined style={{ fontSize: 20, color: "#6584e7" }} />
              </Tooltip>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t("notifications.enableTimerSound")}>
          <Space align="center" size={12}>
            <Badge
              text={
                clientNotificationsSettings.enableTimerSound ? t("notifications.active") : t("notifications.inactive")
              }
              status={clientNotificationsSettings.enableTimerSound ? "processing" : "warning"}
            />
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t("notifications.notificationsAboutWeightMeasurement")}>
          <Space align="center" size={12}>
            <Badge
              text={
                clientNotificationsSettings.activeWeightMeasurementReminder
                  ? t("notifications.active")
                  : t("notifications.inactive")
              }
              status={clientNotificationsSettings.activeWeightMeasurementReminder ? "processing" : "warning"}
            />
            {clientNotificationsSettings.activeWeightMeasurementReminder && (
              <Tooltip title={t("notifications.aboutWeightNotification")}>
                <InfoCircleOutlined style={{ fontSize: 20, color: "#6584e7" }} />
              </Tooltip>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t("notifications.reminderAboutWeightMeasurement")}>
          {clientNotificationsSettings.activeWeightMeasurementReminder
            ? t(NOTIFICATIONS_FREQUENCY_LABELS[clientNotificationsSettings.weightMeasurementFrequency])
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("notifications.notificationsAboutBodyMeasurement")}>
          <Space align="center" size={12}>
            <Badge
              text={
                clientNotificationsSettings.activeBodyMeasurementReminder
                  ? t("notifications.active")
                  : t("notifications.inactive")
              }
              status={clientNotificationsSettings.activeBodyMeasurementReminder ? "processing" : "warning"}
            />
            {clientNotificationsSettings.activeBodyMeasurementReminder && (
              <Tooltip title={t("notifications.aboutBodyNotification")}>
                <InfoCircleOutlined style={{ fontSize: 20, color: "#6584e7" }} />
              </Tooltip>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t("notifications.reminderAboutBodyMeasurement")}>
          {clientNotificationsSettings.activeBodyMeasurementReminder
            ? t(NOTIFICATIONS_FREQUENCY_LABELS[clientNotificationsSettings.bodyMeasurementFrequency])
            : "-"}
        </Descriptions.Item>
      </Descriptions>

      <div className="mt-6 pl-2.5">
        <Typography.Text type="secondary">{t("notifications.clientInfo")}</Typography.Text>
      </div>

      <ModalForm
        visible={isFormVisible}
        loading={updateSettings.isLoading}
        onCancel={() => setIsFormVisible(false)}
        title={t("notifications.formTitle")}
      >
        <TraineeNotificationsSettingsForm onSubmit={handleSubmit} model={clientNotificationsSettings} />
      </ModalForm>
    </Card>
  );
};

export default TraineeNotifications;
